import React, { useState, useEffect, useCallback, useRef } from "react";
import { User } from "../types/api";
import { Layout } from "../components/layout/Layout";
import { CustomButton } from "../components/ui/CustomButton";

const API_URL = process.env.REACT_APP_API_URL || "https://dev-backend.taliaz-cm.com";

type SortKey = "user_name" | "connected_at" | "ip" | "browser" | "os";

interface Connection {
    user_id: number;
    user_name: string;
    connected_at: string;
    ip?: string;
    browser?: string;
    os?: string;
}

interface ConnectionsResponse {
    total_connections: number;
    connected_users: number;
    connections: Connection[];
}

interface ConnectionsScreenProps {
    user: User;
    onLogout: () => void;
    onNavigate?: (
        screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections",
        patientId?: string | number,
        meetingId?: string | number
    ) => void;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

const DEFAULT_WIDTHS: Record<SortKey, number> = {
    user_name: 160,
    connected_at: 190,
    ip: 130,
    browser: 120,
    os: 120,
};

export const ConnectionsScreen: React.FC<ConnectionsScreenProps> = ({
    user,
    onLogout,
    onNavigate,
    taskCount,
    whatsappUnreadCount,
}) => {
    const [data, setData] = useState<ConnectionsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [sortColumn, setSortColumn] = useState<SortKey>("connected_at");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const [columnWidths, setColumnWidths] = useState<Record<SortKey, number>>(DEFAULT_WIDTHS);

    const [resizingColumn, setResizingColumn] = useState<SortKey | null>(null);
    const [startX, setStartX] = useState(0);
    const [startWidth, setStartWidth] = useState(0);

    const token = localStorage.getItem("token") || "";

    const formatDateTime = (isoString: string) => {
        if (!isoString) return "-";
        try {
            const date = new Date(isoString);
            return (
                date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) +
                " " +
                date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
            );
        } catch {
            return isoString;
        }
    };

    const fetchConnections = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/events/connections?token=${encodeURIComponent(token)}`);
            if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
            const json: ConnectionsResponse = await response.json();
            setData(json);
        } catch (err: any) {
            setError(err?.message || "Failed to load connections.");
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchConnections(); }, [fetchConnections]);

    // Column resize mouse handlers
    useEffect(() => {
        if (!resizingColumn) return;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        const onMouseMove = (e: MouseEvent) => {
            const newWidth = Math.max(60, startWidth + (e.clientX - startX));
            setColumnWidths(prev => ({ ...prev, [resizingColumn]: newWidth }));
        };
        const onMouseUp = () => {
            setResizingColumn(null);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [resizingColumn, startX, startWidth]);

    const handleSort = (column: SortKey) => {
        if (sortColumn === column) {
            setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortOrder("asc");
        }
    };

    const handleResizeStart = (e: React.MouseEvent, column: SortKey, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(column);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    const sortedConnections = data
        ? [...data.connections].sort((a, b) => {
            const av = (a[sortColumn] ?? "").toString().toLowerCase();
            const bv = (b[sortColumn] ?? "").toString().toLowerCase();
            return sortOrder === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        })
        : [];

    const ResizableHeader = ({ column, label }: { column: SortKey; label: string }) => {
        const thRef = useRef<HTMLTableCellElement>(null);
        const isSorted = sortColumn === column;
        return (
            <th
                ref={thRef}
                onClick={(e) => {
                    const t = e.target as HTMLElement;
                    if (t.classList.contains("cursor-col-resize") || t.closest(".cursor-col-resize")) return;
                    handleSort(column);
                }}
                className="relative px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap hover:bg-gray-100 cursor-pointer select-none"
                style={{ width: columnWidths[column], minWidth: columnWidths[column], maxWidth: columnWidths[column] }}
            >
                <div className="flex items-center gap-1">
                    {label}
                    {isSorted && <span className="text-blue-600">{sortOrder === "asc" ? "↑" : "↓"}</span>}
                </div>
                <div
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                    onMouseDown={(e) => handleResizeStart(e, column, thRef.current?.offsetWidth || columnWidths[column])}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    };

    return (
        <Layout
            userName={user.name || user.email}
            onLogout={onLogout}
            activeMenuItem="connections"
            onNavigate={onNavigate}
            institutionId={user.institution_id}
            user={user}
            taskCount={taskCount}
            whatsappUnreadCount={whatsappUnreadCount}
        >
            <div className="p-6">
                <div className="bg-white rounded-lg shadow-sm">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-semibold text-gray-900">SSE Connections</h1>
                                {data && !isLoading && (
                                    <p className="mt-1 text-sm text-gray-500">
                                        {data.total_connections} connection{data.total_connections !== 1 ? "s" : ""} from{" "}
                                        {data.connected_users} user{data.connected_users !== 1 ? "s" : ""}
                                    </p>
                                )}
                            </div>
                            <CustomButton variant="secondary" size="sm" onClick={fetchConnections} disabled={isLoading}>
                                {isLoading ? "Refreshing..." : "Refresh"}
                            </CustomButton>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="overflow-x-auto">
                        {isLoading && (
                            <div className="flex items-center justify-center py-16 text-gray-500">
                                <svg className="animate-spin h-5 w-5 mr-3 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                                Loading connections...
                            </div>
                        )}

                        {!isLoading && error && (
                            <div className="flex flex-col items-center justify-center py-16 text-red-600 gap-3">
                                <p className="text-sm font-medium">{error}</p>
                                <CustomButton variant="secondary" size="sm" onClick={fetchConnections}>Retry</CustomButton>
                            </div>
                        )}

                        {!isLoading && !error && data && data.connections.length === 0 && (
                            <div className="text-center py-12 text-gray-600">No active connections</div>
                        )}

                        {!isLoading && !error && data && data.connections.length > 0 && (
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-200" style={{ tableLayout: "fixed" }}>
                                <thead className="bg-gray-50">
                                    <tr>
                                        <ResizableHeader column="user_name" label="User Name" />
                                        <ResizableHeader column="connected_at" label="Connected At" />
                                        <ResizableHeader column="ip" label="IP" />
                                        <ResizableHeader column="browser" label="Browser" />
                                        <ResizableHeader column="os" label="OS" />
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {sortedConnections.map((conn, idx) => (
                                        <tr key={`${conn.user_id}-${conn.connected_at}-${idx}`} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: columnWidths.user_name }}>
                                                {conn.user_name || String(conn.user_id)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: columnWidths.connected_at }}>
                                                {formatDateTime(conn.connected_at)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: columnWidths.ip }}>
                                                {conn.ip || "-"}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: columnWidths.browser }}>
                                                {conn.browser || "-"}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: columnWidths.os }}>
                                                {conn.os || "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {!isLoading && !error && data && (
                        <div className="px-6 py-4 border-t border-gray-200 text-sm text-gray-600">
                            Showing {data.connections.length} connection{data.connections.length !== 1 ? "s" : ""}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};
